import _slicedToArray from "/home/hyuto/Hyuto.github.io/v3-beta/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _asyncToGenerator from "/home/hyuto/Hyuto.github.io/v3-beta/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _regeneratorRuntime from "/home/hyuto/Hyuto.github.io/v3-beta/node_modules/@babel/runtime/regenerator/index.js";

/**
 * @license
 * Copyright 2020 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import { nonMaxSuppressionV4Impl } from '../../backends/non_max_suppression_impl';
import { convertToTensor } from '../../tensor_util_env';
import { nonMaxSuppSanityCheck } from '../nonmax_util';
import { scalar } from '../scalar';
import { tensor1d } from '../tensor1d';
/**
 * Asynchronously performs non maximum suppression of bounding boxes based on
 * iou (intersection over union), with an option to pad results.
 *
 * @param boxes a 2d tensor of shape `[numBoxes, 4]`. Each entry is
 *     `[y1, x1, y2, x2]`, where `(y1, x1)` and `(y2, x2)` are the corners of
 *     the bounding box.
 * @param scores a 1d tensor providing the box scores of shape `[numBoxes]`.
 * @param maxOutputSize The maximum number of boxes to be selected.
 * @param iouThreshold A float representing the threshold for deciding whether
 *     boxes overlap too much with respect to IOU. Must be between [0, 1].
 *     Defaults to 0.5 (50% box overlap).
 * @param scoreThreshold A threshold for deciding when to remove boxes based
 *     on score. Defaults to -inf, which means any score is accepted.
 * @param padToMaxOutputSize Defalts to false. If true, size of output
 *     `selectedIndices` is padded to maxOutputSize.
 * @return A map with the following properties:
 *     - selectedIndices: A 1D tensor with the selected box indices.
 *     - validOutputs: A scalar denoting how many elements in `selectedIndices`
 *       are valid. Valid elements occur first, then padding.
 *
 * @doc {heading: 'Operations', subheading: 'Images', namespace: 'image'}
 */

function nonMaxSuppressionPaddedAsync_(_x, _x2, _x3) {
  return _nonMaxSuppressionPaddedAsync_.apply(this, arguments);
}

function _nonMaxSuppressionPaddedAsync_() {
  _nonMaxSuppressionPaddedAsync_ = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(boxes, scores, maxOutputSize) {
    var iouThreshold,
        scoreThreshold,
        padToMaxOutputSize,
        $boxes,
        $scores,
        params,
        $maxOutputSize,
        $iouThreshold,
        $scoreThreshold,
        _yield$Promise$all,
        _yield$Promise$all2,
        boxesVals,
        scoresVals,
        _nonMaxSuppressionV4I,
        selectedIndices,
        validOutputs,
        _args = arguments;

    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            iouThreshold = _args.length > 3 && _args[3] !== undefined ? _args[3] : 0.5;
            scoreThreshold = _args.length > 4 && _args[4] !== undefined ? _args[4] : Number.NEGATIVE_INFINITY;
            padToMaxOutputSize = _args.length > 5 && _args[5] !== undefined ? _args[5] : false;
            $boxes = convertToTensor(boxes, 'boxes', 'nonMaxSuppressionAsync');
            $scores = convertToTensor(scores, 'scores', 'nonMaxSuppressionAsync');
            params = nonMaxSuppSanityCheck($boxes, $scores, maxOutputSize, iouThreshold, scoreThreshold, null
            /* softNmsSigma */
            );
            $maxOutputSize = params.maxOutputSize;
            $iouThreshold = params.iouThreshold;
            $scoreThreshold = params.scoreThreshold;
            _context.next = 11;
            return Promise.all([$boxes.data(), $scores.data()]);

          case 11:
            _yield$Promise$all = _context.sent;
            _yield$Promise$all2 = _slicedToArray(_yield$Promise$all, 2);
            boxesVals = _yield$Promise$all2[0];
            scoresVals = _yield$Promise$all2[1];
            // We call a cpu based impl directly with the typedarray data here rather
            // than a kernel because all kernels are synchronous (and thus cannot await
            // .data()).
            _nonMaxSuppressionV4I = nonMaxSuppressionV4Impl(boxesVals, scoresVals, $maxOutputSize, $iouThreshold, $scoreThreshold, padToMaxOutputSize), selectedIndices = _nonMaxSuppressionV4I.selectedIndices, validOutputs = _nonMaxSuppressionV4I.validOutputs;

            if ($boxes !== boxes) {
              $boxes.dispose();
            }

            if ($scores !== scores) {
              $scores.dispose();
            }

            return _context.abrupt("return", {
              selectedIndices: tensor1d(selectedIndices, 'int32'),
              validOutputs: scalar(validOutputs, 'int32')
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));
  return _nonMaxSuppressionPaddedAsync_.apply(this, arguments);
}

export var nonMaxSuppressionPaddedAsync = nonMaxSuppressionPaddedAsync_;